<template>
  <div class="renderPage pa-4">

    <!-- WELCOME BANNER -->
    <v-card class="welcome-banner rounded-xl mb-4 overflow-hidden" elevation="0">
      <v-row no-gutters align="center" class="px-6 py-5">
        <v-col cols="12" sm="6">
          <p class="welcome-label mb-1">Bienvenido de vuelta</p>
          <h1 class="welcome-name mb-3">{{ user.nombre || 'Usuario' }}</h1>
          <div class="d-flex align-center flex-wrap" style="gap:8px">
            <v-chip small class="welcome-chip font-weight-bold">
              <v-icon x-small left>mdi-shield-check</v-icon>
              {{ dashboardInfo.plan || '...' }}
            </v-chip>
            <span class="welcome-expiry">
              <v-icon x-small class="mr-1">mdi-calendar-clock</v-icon>
              {{ fechaVencimientoFmt }}
            </span>
          </div>
        </v-col>

        <v-col cols="12" sm="6" class="d-flex justify-end">
          <div class="text-right">
            <p class="banner-total-label mb-1">Total facturado este mes</p>
            <p class="banner-total-amount mb-2">₡{{ estadosInfo.totalAceptadosStr || '0.00' }}</p>
            <div class="d-flex justify-end align-center flex-wrap" style="gap:8px">
              <span class="banner-stat-pill banner-stat-success">
                <v-icon x-small class="mr-1">mdi-check-circle</v-icon>
                {{ estadosInfo.aceptados || 0 }} aceptados
              </span>
              <span v-if="estadosInfo.rechazados > 0" class="banner-stat-pill banner-stat-error">
                <v-icon x-small class="mr-1">mdi-close-circle</v-icon>
                {{ estadosInfo.rechazados }} rechazados
              </span>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <!-- KPI CARDS -->
    <v-row class="mb-4">
      <v-col cols="12" sm="3">
        <v-card class="kpi-card rounded-xl pa-4" elevation="1">
          <div class="d-flex align-center justify-space-between">
            <div class="flex-grow-1 mr-3">
              <p class="kpi-label mb-2">Aceptados este mes</p>
              <p class="kpi-value kpi-value--success mb-1">{{ estadosInfo.aceptados || 0 }}</p>
              <p class="kpi-sub">₡{{ estadosInfo.totalAceptadosStr || '0.00' }}</p>
            </div>
            <div class="kpi-icon-wrap kpi-icon-success">
              <v-icon color="success" size="22">mdi-check-circle-outline</v-icon>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="3">
        <v-card class="kpi-card rounded-xl pa-4" elevation="1">
          <div class="d-flex align-center justify-space-between">
            <div class="flex-grow-1 mr-3">
              <p class="kpi-label mb-2">Rechazados este mes</p>
              <p class="kpi-value kpi-value--error mb-1">{{ estadosInfo.rechazados || 0 }}</p>
              <p class="kpi-sub">Requieren atención</p>
            </div>
            <div class="kpi-icon-wrap kpi-icon-error">
              <v-icon color="error" size="22">mdi-close-circle-outline</v-icon>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="3">
        <v-card class="kpi-card rounded-xl pa-4" elevation="1">
          <div class="d-flex align-center justify-space-between">
            <div class="flex-grow-1 mr-3">
              <p class="kpi-label mb-2">Pendientes este mes</p>
              <p class="kpi-value kpi-value--warning mb-1">{{ estadosInfo.pendientes || 0 }}</p>
              <p class="kpi-sub">En proceso</p>
            </div>
            <div class="kpi-icon-wrap kpi-icon-warning">
              <v-icon color="warning" size="22">mdi-clock-outline</v-icon>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="3">
        <v-card class="kpi-card rounded-xl pa-4" elevation="1">
          <div class="d-flex align-center justify-space-between">
            <div class="flex-grow-1 mr-3">
              <p class="kpi-label mb-2">Docs disponibles</p>
              <p class="kpi-value kpi-value--primary mb-1">{{ docsDisponiblesLabel }}</p>
              <p class="kpi-sub">Usados: {{ docsConsumidosLabel }}</p>
            </div>
            <div class="kpi-icon-wrap kpi-icon-primary">
              <v-icon color="#37474f" size="22">mdi-file-document-multiple-outline</v-icon>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- CHART + SIDEBAR -->
    <v-row>
      <v-col cols="12" md="8">
        <!-- Gráfico -->
        <v-card class="rounded-xl pa-5 mb-4" elevation="1">
          <div class="d-flex align-center mb-4">
            <div>
              <h2 class="section-title mb-0">Resumen de Facturación</h2>
              <p class="caption grey--text mb-0">Documentos aceptados por mes en el año actual</p>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon small color="grey" @click="getFacturasInfo()">
              <v-icon small>mdi-refresh</v-icon>
            </v-btn>
          </div>
          <div class="chart-container">
            <Bar
              v-if="chartData && chartData.labels"
              :chart-options="chartOptions"
              :chart-data="formattedChartData"
              :chart-id="chartId"
              :width="width"
              :height="300"
            />
            <div v-else class="d-flex align-center justify-center" style="height:300px">
              <div class="text-center">
                <v-icon size="48" color="grey lighten-2">mdi-chart-bar</v-icon>
                <p class="mt-2 caption grey--text">Cargando datos...</p>
              </div>
            </div>
          </div>
        </v-card>

        <!-- Documentos Recientes -->
        <v-card class="rounded-xl pa-5" elevation="1">
          <div class="d-flex align-center mb-3">
            <h2 class="section-title mb-0">Documentos Recientes</h2>
            <span class="caption grey--text ml-2">(últimos 30 días)</span>
            <v-spacer></v-spacer>
            <v-btn icon small color="grey" @click="getRecentDocs()">
              <v-icon small>mdi-refresh</v-icon>
            </v-btn>
          </div>
          <div v-if="recentDocs.length === 0" class="text-center py-6">
            <v-icon size="40" color="grey lighten-2">mdi-file-search-outline</v-icon>
            <p class="caption grey--text mt-2">Sin documentos recientes</p>
          </div>
          <template v-for="(doc, i) in recentDocs">
            <div :key="'row-'+i" class="recent-row py-2">
              <div class="d-flex align-center" style="min-width:0; flex:1">
                <div class="doc-type-badge mr-3" :class="getDocBadgeClass(doc.docNombre)">
                  {{ getDocShortName(doc.docNombre) }}
                </div>
                <div style="min-width:0">
                  <p class="recent-code mb-0">{{ doc.codigoFact }}</p>
                  <p class="recent-client mb-0">{{ doc.clienteNombre }}</p>
                </div>
              </div>
              <div class="d-flex flex-column align-end flex-shrink-0 ml-3">
                <p class="recent-amount mb-1">₡{{ doc.totalComprobanteStr }}</p>
                <v-chip x-small :color="getEstadoColor(doc.estadoHacienda)" text-color="white" class="font-weight-bold">
                  {{ doc.estadoHacienda || 'PENDIENTE' }}
                </v-chip>
              </div>
            </div>
            <v-divider v-if="i < recentDocs.length - 1" :key="'div-'+i"></v-divider>
          </template>
        </v-card>
      </v-col>

      <!-- SIDEBAR -->
      <v-col cols="12" md="4">
        <!-- Uso de documentos -->
        <v-card class="rounded-xl pa-5 mb-4" elevation="1">
          <p class="section-title mb-3">Uso de Documentos</p>

          <template v-if="esIlimitado">
            <div class="d-flex align-center mb-3">
              <v-icon color="#37474f" class="mr-2">mdi-infinity</v-icon>
              <span class="caption grey--text">Plan ilimitado — sin restricciones</span>
            </div>
            <div class="d-flex justify-space-between align-center">
              <div class="text-center flex-grow-1">
                <p class="text-h6 font-weight-bold mb-0" style="color:#37474f">∞</p>
                <p class="caption grey--text mb-0">Disponibles</p>
              </div>
              <v-divider vertical class="mx-3" style="height:36px"></v-divider>
              <div class="text-center flex-grow-1">
                <p class="text-h6 font-weight-bold mb-0">{{ docsConsumidosLabel }}</p>
                <p class="caption grey--text mb-0">Usados</p>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="d-flex justify-space-between align-center mb-1">
              <span class="caption grey--text">Progreso de consumo</span>
              <span class="caption font-weight-bold" style="color:#37474f">{{ docUsagePercent }}%</span>
            </div>
            <v-progress-linear
              :value="docUsagePercent"
              color="#37474f"
              :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-3'"
              rounded
              height="6"
              class="mb-4"
            ></v-progress-linear>
            <v-row no-gutters>
              <v-col cols="4" class="text-center">
                <p class="text-h6 font-weight-bold mb-0" style="color:#37474f">{{ docsDisponiblesLabel }}</p>
                <p class="caption grey--text mb-0">Disponibles</p>
              </v-col>
              <v-col cols="4" class="text-center sidebar-mid-col">
                <p class="text-h6 font-weight-bold mb-0">{{ docsConsumidosLabel }}</p>
                <p class="caption grey--text mb-0">Usados</p>
              </v-col>
              <v-col cols="4" class="text-center">
                <p class="text-h6 font-weight-bold mb-0">{{ totalDocs }}</p>
                <p class="caption grey--text mb-0">Total</p>
              </v-col>
            </v-row>
          </template>
        </v-card>

        <!-- Detalles del plan -->
        <v-card class="rounded-xl pa-5" elevation="1">
          <p class="section-title mb-3">Detalles del Plan</p>

          <div class="plan-row py-1">
            <div class="d-flex align-center">
              <v-icon small color="#37474f" class="mr-2">mdi-tag-outline</v-icon>
              <span class="caption grey--text">Plan</span>
            </div>
            <span class="caption font-weight-bold">{{ dashboardInfo.plan || '—' }}</span>
          </div>
          <v-divider class="my-1"></v-divider>

          <div class="plan-row py-1">
            <div class="d-flex align-center">
              <v-icon small color="#37474f" class="mr-2">mdi-calendar-check-outline</v-icon>
              <span class="caption grey--text">Vencimiento</span>
            </div>
            <span class="caption font-weight-bold">{{ fechaVencimientoFmt }}</span>
          </div>
          <v-divider class="my-1"></v-divider>

          <div class="plan-row py-1">
            <div class="d-flex align-center">
              <v-icon small color="#37474f" class="mr-2">mdi-headset</v-icon>
              <span class="caption grey--text">Soporte</span>
            </div>
            <span class="caption font-weight-bold">{{ dashboardInfo.soporte || '—' }}</span>
          </div>
          <v-divider class="my-1"></v-divider>

          <div class="plan-row py-1">
            <div class="d-flex align-center">
              <v-icon small color="#37474f" class="mr-2">mdi-account-multiple-outline</v-icon>
              <span class="caption grey--text">Usuarios</span>
            </div>
            <span class="caption font-weight-bold">{{ dashboardInfo.usuarios || '—' }}</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- FOOTER -->
    <v-footer color="transparent" class="mt-8 justify-center pb-4">
      <div class="text-center">
        <div class="mb-3">
          <v-btn v-for="icon in icons" :key="icon" class="mx-2" icon color="grey darken-1" @click="selectedIcon(icon)">
            <v-icon size="20">{{ icon }}</v-icon>
          </v-btn>
        </div>
        <p class="caption grey--text mb-0">
          &copy; {{ new Date().getFullYear() }} — <span class="font-weight-bold">Eduva Solutions</span>.
          V-{{ $root.version || '1.1.0' }}
        </p>
      </div>
    </v-footer>

    <Snackbar v-bind:text="snackText" v-bind:snackbar.sync="snackValue" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import { Bar } from "vue-chartjs/legacy";
import Snackbar from "./shared/Snackbar.vue";

import {
  Chart as ChartJS, Title, Tooltip, Legend,
  BarElement, CategoryScale, LinearScale,
} from "chart.js";

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

export default {
  name: "Dashboard",
  components: { Bar, Snackbar },
  props: {
    chartId: { type: String, default: "bar-chart" },
    width:   { type: Number, default: 400 },
  },
  data() {
    return {
      icons: ["mdi-facebook", "mdi-whatsapp", "mdi-youtube"],
      snackText: "",
      snackValue: false,
      dashboardInfo: {},
      estadosInfo: {},
      recentDocs: [],
      chartData: {},
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          y: { beginAtZero: true, grid: { display: false } },
          x: { grid: { display: false } },
        },
      },
    };
  },
  computed: {
    ...mapGetters(["user", "strCia"]),

    // El API devuelve strings con prefijos ("Disponibles: Ilimitado", "Realizados: 49")
    // Estos computed extraen solo el valor limpio
    docsDisponiblesLabel() {
      const raw = this.dashboardInfo.disponibleDocumentos || '0';
      return raw.replace(/^Disponibles:\s*/i, '').trim();
    },
    docsConsumidosLabel() {
      const raw = this.dashboardInfo.consumidosDocumentos || '0';
      return raw.replace(/^Realizados:\s*/i, '').trim();
    },
    fechaVencimientoFmt() {
      const raw = this.dashboardInfo.fechaVencimientoStr || '';
      return raw.replace(/^Vencimiento:\s*/i, '').trim() || '—';
    },
    esIlimitado() {
      return this.docsDisponiblesLabel.toLowerCase().includes('ilimitado') ||
             this.docsDisponiblesLabel === '∞';
    },
    totalDocs() {
      if (this.esIlimitado) return parseInt(this.docsConsumidosLabel) || 0;
      const disponible = parseInt(this.docsDisponiblesLabel) || 0;
      const consumidos = parseInt(this.docsConsumidosLabel) || 0;
      return disponible + consumidos;
    },
    docUsagePercent() {
      if (this.esIlimitado || this.totalDocs === 0) return 0;
      const consumidos = parseInt(this.docsConsumidosLabel) || 0;
      return Math.round((consumidos / this.totalDocs) * 100);
    },
    formattedChartData() {
      if (!this.chartData || !this.chartData.datasets) return { labels: [], datasets: [] };
      const datasets = this.chartData.datasets.map(ds => ({
        ...ds,
        backgroundColor: '#37474f',
        borderRadius: 8,
        barThickness: 32,
      }));
      return { labels: this.chartData.labels, datasets };
    },
  },
  watch: {
    strCia(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.getData();
        this.getFacturasInfo();
        this.getEstadosInfo();
        this.getRecentDocs();
      }
    },
  },
  created() {
    this.getData();
    this.getFacturasInfo();
    this.getEstadosInfo();
    this.getRecentDocs();
  },
  methods: {
    getData() {
      axios.get("/api/General/getDashboardPlanInfo?cia=" + this.strCia)
        .then(r => { this.dashboardInfo = r.data; })
        .catch(e => console.error(e));
    },
    getFacturasInfo() {
      axios.get("/api/General/getDashboardFacturasInfo?cia=" + this.strCia)
        .then(r => { this.chartData = r.data; })
        .catch(e => console.error(e));
    },
    getEstadosInfo() {
      axios.get("/api/General/getDashboardEstadosInfo?cia=" + this.strCia)
        .then(r => { this.estadosInfo = r.data; })
        .catch(e => console.error(e));
    },
    getRecentDocs() {
      axios.get("/api/General/getDashboardRecentDocs?cia=" + this.strCia)
        .then(r => { this.recentDocs = r.data; })
        .catch(e => console.error(e));
    },
    getEstadoColor(estado) {
      if (estado === "ACEPTADO")   return "success";
      if (estado === "RECHAZADO")  return "error";
      if (estado === "PROCESANDO") return "warning";
      return "grey";
    },
    getDocShortName(docNombre) {
      return { Facturas: "FE", Tiquetes: "TE", NotasCredito: "NC", NotasDebito: "ND" }[docNombre] || "??";
    },
    getDocBadgeClass(docNombre) {
      return { Facturas: "badge-fe", Tiquetes: "badge-te", NotasCredito: "badge-nc", NotasDebito: "badge-nd" }[docNombre] || "";
    },
    selectedIcon(icon) {
      const urls = {
        "mdi-facebook": "https://www.facebook.com/Eduva-Solutions-109625341739954",
        "mdi-whatsapp": "https://api.whatsapp.com/send/?phone=50664804183",
        "mdi-youtube":  "https://www.youtube.com/channel/UCRJVKCvSgBxIgaZhFZtCvmQ",
      };
      if (urls[icon]) window.open(urls[icon], "_blank");
    },
  },
};
</script>

<style scoped>
/* ── Welcome Banner ── */
.welcome-banner {
  background: linear-gradient(135deg, #263238 0%, #37474f 55%, #455a64 100%) !important;
}
.theme--dark .welcome-banner {
  background: linear-gradient(135deg, #1a2327 0%, #263238 55%, #37474f 100%) !important;
}
.welcome-label {
  color: rgba(255,255,255,0.7);
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.04em;
}
.welcome-name {
  color: #ffffff;
  font-size: 1.7rem;
  font-weight: 700;
  line-height: 1.2;
}
.welcome-expiry {
  color: rgba(255,255,255,0.8);
  font-size: 0.78rem;
  display: flex;
  align-items: center;
}
.welcome-chip {
  background: rgba(255,255,255,0.18) !important;
  color: #ffffff !important;
  border: 1px solid rgba(255,255,255,0.3) !important;
}
.banner-total-label {
  color: rgba(255,255,255,0.65);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
}
.banner-total-amount {
  color: #ffffff;
  font-size: 2rem;
  font-weight: 800;
  line-height: 1.1;
}
.banner-stat-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 600;
}
.banner-stat-success { background: rgba(255,255,255,0.18); color: #ffffff; }
.banner-stat-error   { background: rgba(254,202,202,0.22); color: #fecaca; }

/* ── KPI Cards ── */
.kpi-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
.kpi-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.09) !important; }

.kpi-label {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #94a3b8;
  font-weight: 600;
  margin: 0;
}
.kpi-value {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.1;
  margin: 0;
}
.kpi-value--primary { color: #37474f; }
.kpi-value--success { color: #059669; }
.kpi-value--error   { color: #dc2626; }
.kpi-value--warning { color: #d97706; }
.theme--dark .kpi-value--primary { color: #90a4ae; }
.theme--dark .kpi-value--success { color: #6ee7b7; }
.theme--dark .kpi-value--error   { color: #fca5a5; }
.theme--dark .kpi-value--warning { color: #fcd34d; }

.kpi-sub {
  font-size: 0.73rem;
  color: #94a3b8;
  margin: 0;
}
.kpi-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kpi-icon-primary { background: rgba(55,71,79,0.09); }
.kpi-icon-success { background: rgba(5,150,105,0.10); }
.kpi-icon-error   { background: rgba(220,38,38,0.10); }
.kpi-icon-warning { background: rgba(217,119,6,0.10); }
.theme--dark .kpi-icon-primary { background: rgba(55,71,79,0.25); }
.theme--dark .kpi-icon-success { background: rgba(5,150,105,0.18); }
.theme--dark .kpi-icon-error   { background: rgba(220,38,38,0.18); }
.theme--dark .kpi-icon-warning { background: rgba(217,119,6,0.18); }

/* ── Section titles ── */
.section-title { font-size: 0.875rem; font-weight: 700; color: #374151; }
.theme--dark .section-title { color: #e2e8f0; }

/* ── Documentos recientes ── */
.recent-row { display: flex; align-items: center; justify-content: space-between; }

.doc-type-badge {
  width: 36px;
  height: 36px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 700;
  flex-shrink: 0;
  letter-spacing: 0.02em;
}
.badge-fe { background: rgba(5,150,105,0.12);  color: #059669; }
.badge-te { background: rgba(14,165,233,0.12); color: #0284c7; }
.badge-nc { background: rgba(245,158,11,0.12); color: #d97706; }
.badge-nd { background: rgba(168,85,247,0.12); color: #9333ea; }
.theme--dark .badge-fe { background: rgba(5,150,105,0.2);  color: #6ee7b7; }
.theme--dark .badge-te { background: rgba(14,165,233,0.2); color: #7dd3fc; }
.theme--dark .badge-nc { background: rgba(245,158,11,0.2); color: #fcd34d; }
.theme--dark .badge-nd { background: rgba(168,85,247,0.2); color: #d8b4fe; }

.recent-code {
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.recent-client {
  font-size: 0.72rem;
  color: #94a3b8;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}
.recent-amount { font-size: 0.82rem; font-weight: 700; color: #37474f; }
.theme--dark .recent-amount { color: #90a4ae; }

/* ── Sidebar ── */
.sidebar-mid-col {
  border-left:  1px solid rgba(0,0,0,0.07);
  border-right: 1px solid rgba(0,0,0,0.07);
}
.theme--dark .sidebar-mid-col {
  border-left:  1px solid rgba(255,255,255,0.08);
  border-right: 1px solid rgba(255,255,255,0.08);
}
.plan-row { display: flex; align-items: center; justify-content: space-between; }

/* ── Chart ── */
.chart-container { position: relative; height: 300px; width: 100%; }
</style>
